"use client";

import { useState } from "react";
import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  AbsorbancePanel,
  ConvertPanel,
  kindLabel,
} from "@/components/tools/biology-lab/nucleic-acid-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { plain } from "@/lib/tool-kit/biology-lab/format";
import {
  A260_FACTOR,
  type AbsorbanceInput,
  convertAmount,
  type MassUnit,
  type NucleicKind,
} from "@/lib/tool-kit/biology-lab/nucleic-acid";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const DNA_CONCENTRATION_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeAbsorbance: "Concentration from absorbance (A260)",
  modeConvert: "Convert ng ↔ pmol",
  kindLabel: "Sample type",
  kindDsdna: "Double-stranded DNA (dsDNA) — plasmid, PCR product, genomic",
  kindSsdna: "Single-stranded DNA (ssDNA)",
  kindRna: "RNA",
  kindOligo: "Oligonucleotide (short ssDNA)",
  kindShortDsdna: "dsDNA",
  kindShortSsdna: "ssDNA",
  kindShortRna: "RNA",
  kindShortOligo: "oligo",
  a260: "A260 reading",
  a280: "A280 reading (optional)",
  a230: "A230 reading (optional)",
  background: "Background A320 (optional)",
  backgroundHint:
    "Subtracted from every reading to correct for turbidity or a drifting baseline.",
  pathLabel: "Path length (mm)",
  pathHint:
    "10 mm for a standard cuvette; a NanoDrop reports a 10 mm equivalent.",
  dilution: "Dilution factor",
  dilutionHint: "1 if you read the sample neat; 10 if you diluted 1 in 10.",
  factorLabel: "Conversion factor override (optional)",
  factorHint: "ng·cm/µL per A260 unit. Leave blank to use {factor}.",
  volume: "Sample volume (µL, optional)",
  volumeHint: "For the total yield.",
  length: "Length (bp for dsDNA, nt otherwise, optional)",
  lengthHint: "Adds molarity and pmol.",
  results: "Results",
  concentration: "Concentration",
  unitNgUl: "ng/µL",
  perMlNote: "1 ng/µL is the same as 1 µg/mL.",
  measuredLine: "{value} ng/µL in the cuvette before the dilution factor",
  totalYield: "Total yield",
  pmolPerUl: "Amount of substance",
  molarity: "Molarity",
  totalPmol: "Total in the sample",
  unitUg: "µg",
  unitPmol: "pmol",
  unitFmol: "fmol",
  unitPmolPerUl: "pmol/µL",
  unitNm: "nM",
  purityHeading: "Purity ratios",
  ratio280: "A260/A280",
  ratio230: "A260/A230",
  ratioNone: "Add an A280 or A230 reading to check purity.",
  expectedDna: "Clean DNA reads about 1.8 (1.85–1.88 in buffer).",
  expectedRna: "Clean RNA reads about 2.0–2.1.",
  expected230: "Clean samples read above 1.8, often 2.0–2.4.",
  ok: "Within the usual range.",
  low280:
    "Low — protein, phenol or another contaminant that absorbs near 280 nm is the usual cause.",
  high280Dna:
    "High — RNA carried over into a DNA prep raises the ratio; a very high value can also be a blanking problem.",
  high280Rna:
    "High — check the blank and the sample pH; an acidic solution reads lower and a basic one higher.",
  low230:
    "Low — guanidine salts, EDTA, phenol, carbohydrate or detergent absorb at 230 nm. Re-clean the sample or re-check the blank.",
  high230:
    "High — usually a blank in the wrong buffer or a very low A230 reading. Re-blank with the same buffer as the sample.",
  working: "The working",
  workingFormula: "c = A260 × factor × (10 mm ÷ path) × dilution",
  workingNumbers: "c = {a} × {factor} × (10 ÷ {path}) × {dilution} = {c} ng/µL",
  workingMoles: "pmol/µL = {c} × 1,000 ÷ ({mw} × {length}) = {value}",
  warnHigh:
    "A260 above about 1.5 is outside the range where most spectrophotometers stay linear. Dilute the sample and read again.",
  warnLow:
    "A260 below about 0.1 is close to the noise floor of the instrument; the concentration and ratios are unreliable.",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{kind}: {c} ng/µL",
  copyRatio280: "A260/A280 {value}",
  copyRatio230: "A260/A230 {value}",
  copyYield: "{value} µg total in {volume} µL",
  enterA260: "Enter the A260 reading to see the concentration.",
  errPath: "The path length must be greater than 0 mm.",
  errDilution: "The dilution factor must be greater than 0.",
  errFactor: "The conversion factor must be greater than 0.",
  errBackground:
    "The background reading must be 0 or more and less than the A260 reading.",
  errNonPositive: "A260 must be greater than 0 to give a concentration.",
  convertAmount: "Amount",
  convertUnit: "Unit",
  convertLength: "Length (bp for dsDNA, nt otherwise)",
  unitOptionNg: "ng",
  unitOptionUg: "µg",
  unitOptionPmol: "pmol",
  unitOptionFmol: "fmol",
  convertedHeading: "Converted amounts",
  endsLabel: "pmol of DNA ends",
  convertWorking: "{length} {unit} × {mw} g/mol = {total} g/mol",
  unitBp: "bp",
  unitNt: "nt",
  convertEnter: "Enter an amount and a length greater than 0 to convert.",
  convertNote:
    "Uses an average of {mw} g/mol per {unit}. Sequence-exact molecular weights differ by a percent or two.",
} as const;

export type DnaConcentrationCalculatorStrings = {
  [K in keyof typeof DNA_CONCENTRATION_CALCULATOR_STRINGS]: string;
};
type Mode = "absorbance" | "convert";

const ID = "dna";
const KIND_VALUES: readonly NucleicKind[] = ["dsdna", "ssdna", "rna", "oligo"];
export default function DnaConcentrationCalculatorWidget() {
  const strings = useToolUi(DNA_CONCENTRATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("absorbance");
  const [kind, setKind] = useState<NucleicKind>("dsdna");
  const [a260, setA260] = useState("0.412");
  const [a280, setA280] = useState("0.222");
  const [a230, setA230] = useState("0.18");
  const [background, setBackground] = useState("");
  const [path, setPath] = useState("10");
  const [dilution, setDilution] = useState("10");
  const [factor, setFactor] = useState("");
  const [volume, setVolume] = useState("50");
  const [length, setLength] = useState("5000");
  const [amount, setAmount] = useState("1");
  const [unit, setUnit] = useState<MassUnit>("ug");
  const [convLength, setConvLength] = useState("1000");

  const kindOptions = KIND_VALUES.map((value) => ({
    value,
    label: kindLabel(value, strings),
  }));
  const unitOptions: { value: MassUnit; label: string }[] = [
    { value: "ng", label: strings.unitOptionNg },
    { value: "ug", label: strings.unitOptionUg },
    { value: "pmol", label: strings.unitOptionPmol },
    { value: "fmol", label: strings.unitOptionFmol },
  ];

  const a260Value = parseMeasuredNumber(a260);
  const input: AbsorbanceInput | null =
    a260Value === null
      ? null
      : {
          kind,
          a260: a260Value,
          a280: parseMeasuredNumber(a280),
          a230: parseMeasuredNumber(a230),
          background: parseMeasuredNumber(background),
          pathMm: parseMeasuredNumber(path) ?? 0,
          dilution: parseMeasuredNumber(dilution) ?? 0,
          factorOverride: parseMeasuredNumber(factor),
          volumeUl: parseMeasuredNumber(volume),
          length: parseMeasuredNumber(length),
        };

  const amountValue = parseMeasuredNumber(amount);
  const convLengthValue = parseMeasuredNumber(convLength);
  const amounts =
    amountValue === null || convLengthValue === null
      ? null
      : convertAmount(amountValue, unit, kind, convLengthValue);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name={`${ID}-mode`}
        onChange={setMode}
        options={[
          { value: "absorbance", label: strings.modeAbsorbance },
          { value: "convert", label: strings.modeConvert },
        ]}
        value={mode}
      />
      <SelectField
        id={`${ID}-kind`}
        label={strings.kindLabel}
        onChange={setKind}
        options={kindOptions}
        value={kind}
      />
      {mode === "absorbance" ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id={`${ID}-a260`}
              label={strings.a260}
              numeric
              onChange={setA260}
              value={a260}
            />
            <TextField
              id={`${ID}-a280`}
              label={strings.a280}
              numeric
              onChange={setA280}
              value={a280}
            />
            <TextField
              id={`${ID}-a230`}
              label={strings.a230}
              numeric
              onChange={setA230}
              value={a230}
            />
            <TextField
              hint={strings.backgroundHint}
              id={`${ID}-bg`}
              label={strings.background}
              numeric
              onChange={setBackground}
              value={background}
            />
            <TextField
              hint={strings.pathHint}
              id={`${ID}-path`}
              label={strings.pathLabel}
              numeric
              onChange={setPath}
              value={path}
            />
            <TextField
              hint={strings.dilutionHint}
              id={`${ID}-dilution`}
              label={strings.dilution}
              numeric
              onChange={setDilution}
              value={dilution}
            />
            <TextField
              hint={strings.volumeHint}
              id={`${ID}-volume`}
              label={strings.volume}
              numeric
              onChange={setVolume}
              value={volume}
            />
            <TextField
              hint={strings.lengthHint}
              id={`${ID}-length`}
              label={strings.length}
              numeric
              onChange={setLength}
              value={length}
            />
          </div>
          <TextField
            hint={t(strings.factorHint, {
              factor: plain(fmt, A260_FACTOR[kind]),
            })}
            id={`${ID}-factor`}
            label={strings.factorLabel}
            numeric
            onChange={setFactor}
            value={factor}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id={`${ID}-amount`}
            label={strings.convertAmount}
            numeric
            onChange={setAmount}
            value={amount}
          />
          <SelectField
            id={`${ID}-unit`}
            label={strings.convertUnit}
            onChange={setUnit}
            options={unitOptions}
            value={unit}
          />
          <TextField
            id={`${ID}-conv-length`}
            label={strings.convertLength}
            numeric
            onChange={setConvLength}
            value={convLength}
          />
        </div>
      )}

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "absorbance" ? (
          <AbsorbancePanel fmt={fmt} input={input} strings={strings} />
        ) : (
          <ConvertPanel
            amounts={amounts}
            fmt={fmt}
            kind={kind}
            length={convLengthValue}
            strings={strings}
          />
        )}
      </section>
    </>
  );
}
